import type { ScriptManager } from 'lib/scriptManager';
import ReactGA from 'react-ga4';

import { AnalyticsBase } from '../base';
import { AnalyticsID } from '../constants';
import type { StandardAnalyticsSink } from '../types';
import type { GaEcommerceItem } from './payloads';
import { createEcommercePayload, itemPayloadFromProduct } from './payloads';

export enum GA4Method {
  EVENT = 'event',
}

export enum GA4Event {
  ADD_TO_CART = 'add_to_cart',
  BEGIN_CHECKOUT = 'begin_checkout',
  REMOVE_FROM_CART = 'remove_from_cart',
  VIEW_ITEM = 'view_item',
  VIEW_CART = 'view_cart',
  COOKIES_CONSENT_STATUS = 'cookie_consent_status',
  CART_UPSELL = 'cart_upsell',
  COOKIES_CONSENT = 'cookie_consent',
  SELECT_ITEM = 'select_item',
  SELECT_PROMOTION = 'select_promotion',
  VIEW_ITEM_LIST = 'view_item_list',
  VIEW_PROMOTION = 'view_promotion',
}

export class GA4Analytics
  extends AnalyticsBase
  implements StandardAnalyticsSink
{
  id = AnalyticsID.GTM;

  scriptManager: ScriptManager;

  constructor(scriptManager: ScriptManager) {
    super();
    this.scriptManager = scriptManager;
  }

  isReady() {
    return this.scriptManager.isScriptLoaded(this.id);
  }

  events = {
    pageView: ({ url }) => {
      ReactGA.send({ hitType: 'pageview', page: url });
      ReactGA.gtag(GA4Method.EVENT, GA4Event.COOKIES_CONSENT_STATUS, {
        consent_level: this.scriptManager.getCondition('cookiesAccepted')
          ? 'all'
          : 'none',
      });
    },

    beginCheckout({ cart }) {
      const ecommercePayload = createEcommercePayload(cart);
      ReactGA.gtag(GA4Method.EVENT, GA4Event.BEGIN_CHECKOUT, {
        ecommerce: {
          ...ecommercePayload,
        },
      });
    },

    selectItem({ index, listId, name }) {
      ReactGA.gtag(GA4Method.EVENT, GA4Event.SELECT_ITEM, {
        ecommerce: {
          items: [
            {
              index,
              item_list_id: listId,
              item_name: name,
            },
          ],
        },
      });
    },

    selectPromotion({ code, product }) {
      ReactGA.gtag(GA4Method.EVENT, GA4Event.SELECT_PROMOTION, {
        ecommerce: {
          items: [
            {
              coupon: code,
              item_name: product.name,
            },
          ],
        },
      });
    },

    viewItemList({ products, listId }) {
      const items = products.map((product, i) => ({
        index: i + 1,
        item_name: product,
        item_list_id: listId,
        item_list_name: listId,
      }));

      return ReactGA.gtag(GA4Method.EVENT, GA4Event.VIEW_ITEM_LIST, {
        ecommerce: {
          items,
          item_list_id: listId,
          item_list_name: listId,
        },
      });
    },

    viewPromotion({ code, product }) {
      ReactGA.gtag(GA4Method.EVENT, GA4Event.VIEW_PROMOTION, {
        ecommerce: {
          items: [
            {
              coupon: code,
              item_name: product.name,
            },
          ],
        },
      });
    },

    addToCart(items) {
      const gaItems = items
        .map(itemPayloadFromProduct)
        .filter(Boolean) as GaEcommerceItem[];

      if (gaItems.length === 0) return;

      ReactGA.gtag(GA4Method.EVENT, GA4Event.ADD_TO_CART, {
        ecommerce: {
          value: gaItems.reduce(
            (acc, item) => acc + item.price * item.quantity,
            0,
          ),
          currency: items[0].product.priceRange.maxVariantPrice.currencyCode,
          items: gaItems,
        },
      });
    },

    removeFromCart(items) {
      const gaItems = items
        .map(itemPayloadFromProduct)
        .filter(Boolean) as GaEcommerceItem[];

      if (gaItems.length === 0) return;

      ReactGA.gtag(GA4Method.EVENT, GA4Event.REMOVE_FROM_CART, {
        ecommerce: {
          value: gaItems.reduce(
            (acc, item) => acc + item.price * item.quantity,
            0,
          ),
          currency: items[0].product.priceRange.maxVariantPrice.currencyCode,
          items: gaItems,
        },
      });
    },

    viewItem({ product, merchandiseId }) {
      const gaItem = itemPayloadFromProduct({
        product,
        merchandiseId,
        quantity: 1,
      });

      if (!gaItem) return;

      ReactGA.gtag(GA4Method.EVENT, GA4Event.VIEW_ITEM, {
        ecommerce: {
          items: [
            {
              item_id: gaItem.item_id,
              item_name: gaItem.item_name,
              item_variant: gaItem.item_variant,
              item_category: gaItem.item_category,
              price: gaItem.price,
            },
          ],
        },
      });
    },

    viewCart({ cart }) {
      const ecommercePayload = createEcommercePayload(cart);

      return ReactGA.gtag(GA4Method.EVENT, GA4Event.VIEW_CART, {
        ecommerce: {
          ...ecommercePayload,
        },
      });
    },
  } satisfies StandardAnalyticsSink['events'];
}
